import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { DollarSign, Ticket, CheckCircle, ExternalLink, Copy } from "lucide-react";
import { api } from "../../lib/api";
import { formatCurrency } from "../../lib/utils-format";

export function EventDashboardPage() {
    const { id } = useParams();
    const { t } = useTranslation();
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get(`/api/organizer/events/${id}/dashboard`).then(({ data }) => setData(data));
    }, [id]);

    if (!data) return <div className="text-muted-foreground">{t("common.loading")}</div>;

    const totalSold = data.ticket_types.reduce((s, t) => s + t.sold, 0);
    const totalChecked = data.ticket_types.reduce((s, t) => s + t.checked_in, 0);
    const totalCapacity = data.ticket_types.reduce((s, t) => s + t.total_quantity, 0);

    const eventUrl = `${window.location.origin}/events/${id}`;
    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(eventUrl);
            toast.success(t("organizer.link_copied"));
        } catch {
            // Fallback for older browsers / insecure contexts
            const ta = document.createElement("textarea");
            ta.value = eventUrl;
            document.body.appendChild(ta);
            ta.select();
            try { document.execCommand("copy"); toast.success(t("organizer.link_copied")); }
            catch { toast.error(t("common.error")); }
            document.body.removeChild(ta);
        }
    };

    return (
        <div data-testid="event-dashboard" className="space-y-6">
            <div className="flex flex-wrap items-center gap-2">
                <Link
                    to={`/events/${id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="event-view-btn"
                    className="btn-outline inline-flex items-center gap-2"
                >
                    <ExternalLink className="h-4 w-4" />
                    {t("organizer.view_event")}
                </Link>
                <button
                    type="button"
                    data-testid="event-copy-link-btn"
                    onClick={copyLink}
                    className="btn-outline inline-flex items-center gap-2"
                >
                    <Copy className="h-4 w-4" />
                    {t("organizer.copy_link")}
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard
                    icon={<DollarSign />}
                    label={t("organizer.total_revenue")}
                    value={formatCurrency(data.total_revenue, data.currency_code)}
                    hint={t("organizer.revenue_hint")}
                />
                <StatCard
                    icon={<Ticket />}
                    label={t("organizer.tickets_sold")}
                    value={`${totalSold} / ${totalCapacity}`}
                />
                <StatCard
                    icon={<CheckCircle />}
                    label={t("organizer.tickets_checked_in")}
                    value={`${totalChecked} / ${totalSold}`}
                />
            </div>

            <div className="bento-card p-5 md:p-6">
                <h3 className="font-bold mb-4">{t("organizer.ticket_types")}</h3>
                <div className="space-y-3">
                    {data.ticket_types.map((tt) => (
                        <div key={tt.id} className="space-y-2">
                            <div className="flex items-center justify-between">
                                <span className="font-semibold">{tt.name}</span>
                                <span className="text-sm text-muted-foreground">{formatCurrency(tt.price, data.currency_code)}</span>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                    <div className="text-muted-foreground text-xs uppercase tracking-wider mb-1">{t("organizer.tickets_sold")}</div>
                                    <Progress value={tt.sold} max={tt.total_quantity} />
                                </div>
                                <div>
                                    <div className="text-muted-foreground text-xs uppercase tracking-wider mb-1">{t("organizer.tickets_checked_in")}</div>
                                    <Progress value={tt.checked_in} max={Math.max(1, tt.sold)} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

const StatCard = ({ icon, label, value, hint }) => (
    <div className="stats-card">
        <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-muted [&>svg]:h-4 [&>svg]:w-4">{icon}</div>
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{label}</span>
        </div>
        <div className="font-heading font-black text-2xl">{value}</div>
        {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
);

const Progress = ({ value, max }) => {
    const pct = max === 0 ? 0 : Math.min(100, (value / max) * 100);
    return (
        <div className="space-y-1">
            <div className="text-sm font-semibold">{value} / {max}</div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-foreground transition-all" style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
};


export function SoldTicketsPage() {
    const { id } = useParams();
    const { t } = useTranslation();
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        const { data } = await api.get(`/api/organizer/events/${id}/sold-tickets`);
        setList(data || []);
        setLoading(false);
    };
    useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading) return <div className="text-muted-foreground">{t("common.loading")}</div>;

    if (list.length === 0) {
        return (
            <div data-testid="sold-tickets-page" className="bento-card p-8 text-center text-muted-foreground">
                {t("organizer.no_sales_yet")}
            </div>
        );
    }

    return (
        <div data-testid="sold-tickets-page" className="space-y-3">
            {/* Mobile: vertical card layout (no horizontal scroll) */}
            <div className="md:hidden space-y-3">
                {list.map((r) => (
                    <div key={r.ticket_id} className="bento-card p-4 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                            <div className="font-bold text-base">{r.ticket_type_name}</div>
                            <span className={`badge-pill ${r.status === "checked_in" ? "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]" : "bg-muted"}`}>
                                {r.status === "checked_in" ? t("organizer.status_checked_in") : t("organizer.status_valid")}
                            </span>
                        </div>
                        <div className="text-sm">{r.customer_name}</div>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span>${Number(r.ticket_type_price).toFixed(2)}</span>
                            <span>{new Date(r.created_at).toLocaleDateString()}</span>
                        </div>
                    </div>
                ))}
            </div>
            {/* Desktop: table */}
            <div className="hidden md:block bento-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-muted">
                        <tr>
                            <th className="text-left p-3 font-semibold">{t("organizer.name")}</th>
                            <th className="text-left p-3 font-semibold">{t("organizer.customer")}</th>
                            <th className="text-left p-3 font-semibold">{t("organizer.price")}</th>
                            <th className="text-left p-3 font-semibold">{t("organizer.status")}</th>
                            <th className="text-left p-3 font-semibold">{t("organizer.date")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((r) => (
                            <tr key={r.ticket_id} className="border-t border-border">
                                <td className="p-3 font-semibold">{r.ticket_type_name}</td>
                                <td className="p-3 text-muted-foreground">{r.customer_name}</td>
                                <td className="p-3">${Number(r.ticket_type_price).toFixed(2)}</td>
                                <td className="p-3">
                                    <span className={`badge-pill ${r.status === "checked_in" ? "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]" : "bg-muted"}`}>
                                        {r.status === "checked_in" ? t("organizer.status_checked_in") : t("organizer.status_valid")}
                                    </span>
                                </td>
                                <td className="p-3 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
